import type { Aabb } from "./generated";
import { Client } from "@gradio/client";
import {
  blobToBase64,
  cropBlobImage,
  fetchAndCacheProtectedImage,
  fillBlobImage,
  padBlobImage,
} from "./images";
import type { TableState } from "../tables/types";

export async function predictImageContent(
  blob: Blob,
  prompt: string,
  temperature: number = 0.1,
) {
  const data = await blobToBase64(blob);
  const app = await Client.connect(import.meta.env.VITE_ACDC2_DEEPSEEK_API_URL);

  // Upload image and prompt
  await app.predict(4, [prompt, [{ name: "my_image.png", data }]]);

  const result = await app.predict(5, [
    null,
    0.9, // top p
    temperature,
    1.1, // repetition penalty
    2048, // max generation tokens
    4096, // max history tokens
    "deepseek-ai/deepseek-vl2-small", // model
  ]);

  return (result.data as [[[string, string]]])[0][0][1];
}

export async function predictAlternative(
  imagePath: string,
  token: string,
  aabb: Aabb,
  languageCode: string,
) {
  const blob = await fetchAndCacheProtectedImage(imagePath, token);

  const croppedBlob = await cropBlobImage(
    blob,
    aabb.left,
    aabb.top,
    aabb.width,
    aabb.height,
  );

  const paddedBlob = await padBlobImage(croppedBlob, 20);

  const croppedAndFilledBlob = await fillBlobImage(paddedBlob, "white");

  return await predictImageContent(
    croppedAndFilledBlob,
    `Write an accessibility alternative text for this <image>, in this language: ${languageCode}`,
  );
}

export async function predictText(
  imagePath: string,
  token: string,
  aabb: Aabb,
) {
  const blob = await fetchAndCacheProtectedImage(imagePath, token);

  const croppedBlob = await cropBlobImage(
    blob,
    aabb.left,
    aabb.top,
    aabb.width,
    aabb.height,
  );

  const paddedBlob = await padBlobImage(croppedBlob, 20);

  const filledBlob = await fillBlobImage(paddedBlob, "white");

  return await predictImageContent(
    filledBlob,
    "Perform OCR on this <image> and return nothing but the recognized text!",
    0,
  );
}

export async function predictLatex(
  imagePath: string,
  token: string,
  aabb: Aabb,
) {
  const blob = await fetchAndCacheProtectedImage(imagePath, token);

  const croppedBlob = await cropBlobImage(
    blob,
    aabb.left,
    aabb.top,
    aabb.width,
    aabb.height,
  );

  const paddedBlob = await padBlobImage(croppedBlob, 20);

  const filledBlob = await fillBlobImage(paddedBlob, "white");

  return await predictImageContent(
    filledBlob,
    "Convert this mathematical formula in this <image> in a LaTeX expression. Output only the expression without the leading square brackets",
    0,
  );
}

export async function predictTable(
  imagePath: string,
  token: string,
  aabb: Aabb,
): Promise<TableState> {
  const blob = await fetchAndCacheProtectedImage(imagePath, token);
  const filledBlob = await fillBlobImage(blob, "white");

  const tableMinX = aabb.left;
  const tableMinY = aabb.top;
  const tableMaxX = aabb.left + aabb.width;
  const tableMaxY = aabb.top + aabb.height;

  const searchParams = new URLSearchParams({
    table_min_x: tableMinX.toString(),
    table_min_y: tableMinY.toString(),
    table_max_x: tableMaxX.toString(),
    table_max_y: tableMaxY.toString(),
  });

  const url = new URL(
    `/recognize/`,
    import.meta.env.VITE_ACDC2_TEDITOR_API_URL,
  );
  url.search = searchParams.toString();

  const formData = new FormData();
  formData.append("document", filledBlob);

  const response = await fetch(url, { method: "POST", body: formData });
  const data = await response.json();
  const prediction: TableState = data.result;

  const predictedTableWidth = prediction.cols.reduce(
    (sum, col) => sum + col.width,
    0,
  );
  const predictedTableHeight = prediction.rows.reduce(
    (sum, row) => sum + row.height,
    0,
  );

  const scaleX = aabb.width / predictedTableWidth;
  const scaleY = aabb.height / predictedTableHeight;

  return {
    ...prediction,
    position: {
      x: tableMinX,
      y: tableMinY,
    },
    cols: prediction.cols.map((col) => ({
      ...col,
      width: col.width * scaleX,
    })),
    rows: prediction.rows.map((row) => ({
      ...row,
      height: row.height * scaleY,
    })),
  };
}
